import BackButton from '@/components/navigation/BackButton';
import Button from '@/components/ui/button-old';
import { Form } from '@/components/ui/forms/form';
import Input from '@/components/ui/forms/input';
import Image from 'next/image';
import PageHeading from '@/components/ui/page-heading';
import routes from '@/config/routes';
import client from '@/data/client';
import { useSettings } from '@/data/settings';
import { siteSettings } from '@/data/static/site-settings';
import { trackEvent } from '@/data/utils/utils';
import GeneralLayout from '@/layouts/_general-layout';
import Seo from '@/layouts/_seo';
import { useIsDarkMode } from '@/lib/hooks/use-is-dark-mode';
import { useIsMounted } from '@/lib/hooks/use-is-mounted';
import type { CreateSubscriberInput, NextPageWithLayout } from '@/types';
import type { GetStaticProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import React, { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import axios from 'axios';
import * as yup from 'yup';

const subscribeFormSchema = yup.object().shape({
  email: yup.string().email().required(),
  portofolioUrl: yup.string().optional(),
  seller: yup.boolean().optional(),
  tags: yup.array().of(yup.string()).optional(),
});

const BecomeSellerPage: NextPageWithLayout = () => {
  const { t } = useTranslation('common');
  const { settings } = useSettings();
  const { lightLogo, darkLogo } = siteSettings;
  const { isDarkMode } = useIsDarkMode();
  const isMounted = useIsMounted();
  let [reset, setReset] = useState<CreateSubscriberInput | null>(null);
  const { mutate } = useMutation(
    (data: CreateSubscriberInput) => axios.post('/api/subscribe-to-newsletter', data),
    {
      onSuccess: () => {
        toast.success('Successfully subscribed to newsletter');
        setReset({
          email: '',
          portofolioUrl: '',
        });
      },
      onError: (error) => {
        toast.error('Oops! Something went wrong');
        console.error(error);
      },
    }
  );
  const onSubmit: SubmitHandler<CreateSubscriberInput> = (values) => {
    mutate({ ...values, seller: true });
    trackEvent('subscribe', {
      method: 'email',
      seller: true,
    });
  };
  return (
    <>
      <Seo
        title="Become a Seller"
        description="Sell your prompts, assets, and more on Prompts Club. Get started now and start earning!"
        url={routes.becomeSeller}
      />
      <div className="mx-auto flex h-full w-full max-w-screen-xl flex-col p-4 sm:p-5">
        <BackButton />
        <div className="flex h-32 justify-center p-10">
          <span
            className="relative overflow-hidden"
            style={{
              width: '100%',
              height: 90,
            }}
          >
            {isMounted && isDarkMode && (
              <Image
                src={darkLogo}
                fill
                loading="eager"
                alt={settings?.siteTitle ?? 'Dark Logo'}
                className="object-contain"
              />
            )}
            {isMounted && !isDarkMode && (
              <Image
                src={lightLogo}
                fill
                loading="eager"
                alt={settings?.siteTitle ?? 'Light Logo'}
                className="object-contain"
              />
            )}
          </span>
        </div>
        <PageHeading
          title="Become a Seller: Opportunity Coming Soon!"
          subtitle="Be the first to know when seller registrations reopen. Join our waitlist for early access and benefits!"
        />
        <div className="w-full pb-16">
          <div className="flex justify-center">
            <Form<CreateSubscriberInput>
              onSubmit={onSubmit}
              validationSchema={subscribeFormSchema}
              resetFields={reset}
            >
              {({ register, formState: { errors } }) => (
                <>
                  <fieldset className="mb-6 grid gap-5 sm:grid-cols-1">
                    <Input
                      label={t('contact-us-email-field') + '*'}
                      type="email"
                      {...register('email')}
                      error={errors.email?.message}
                      className="w-96"
                    />
                    <Input
                      label={'Portofolio URL (optional)'}
                      type="url"
                      {...register('portofolioUrl')}
                      error={errors.portofolioUrl?.message}
                      className="w-96"
                    />
                  </fieldset>
                  <Button
                    type="submit"
                    className="mb-1 w-full flex-1 sm:flex-none md:w-auto"
                  >
                    {/* {t('contact-us-submit-button')} */}
                    Join Waitlist
                  </Button>
                </>
              )}
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

BecomeSellerPage.getLayout = function getLayout(page) {
  return <GeneralLayout>{page}</GeneralLayout>;
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale!, ['common'])),
    },
    revalidate: 60, // In seconds
  };
};

export default BecomeSellerPage;
